import React from 'react'
import Prism from 'prismjs'
import 'prismjs/components/prism-python'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'

const useStyles = makeStyles(theme => ({
  code: {
    margin: 0,
    padding: theme.spacing(2),
    borderRadius: 6,
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize: '0.82rem',
    lineHeight: 1.55,
    whiteSpace: 'pre',
    overflowX: 'auto',
    textAlign: 'left',
    backgroundColor: theme.palette.action.hover,
    // Prism token palette, scoped to this block. JSS instead of the stock
    // prism.css so nothing global changes and the colors sit on our light
    // background.
    '& .token.comment': { color: '#708090' },
    '& .token.string': { color: '#669900' },
    '& .token.triple-quoted-string': { color: '#669900' },
    '& .token.keyword': { color: '#0077aa' },
    '& .token.builtin': { color: '#0077aa' },
    '& .token.boolean': { color: '#990055' },
    '& .token.number': { color: '#990055' },
    '& .token.function': { color: '#dd4a68' },
    '& .token.class-name': { color: '#dd4a68' },
    '& .token.decorator': { color: '#9a6e3a' },
    '& .token.operator': { color: '#9a6e3a' },
    '& .token.punctuation': { color: '#999999' },
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(6, 2),
  },
  hint: { flexGrow: 1, textAlign: 'left', paddingLeft: theme.spacing(2) },
}))

/** One Prism token (possibly nested) as React nodes — no innerHTML involved. */
function tokenToNodes(token, key) {
  if (typeof token === 'string') {
    return token
  }
  const inner = Array.isArray(token.content) ?
    token.content.map((t, i) => tokenToNodes(t, `${key}-${i}`)) :
    tokenToNodes(token.content, `${key}-c`)
  return (
    <span key={key} className={`token ${token.type}`}>{inner}</span>
  )
}

/** Python source as highlighted nodes; falls back to plain text on any error. */
function highlightPython(source) {
  try {
    return Prism.tokenize(source, Prism.languages.python)
      .map((token, i) => tokenToNodes(token, `t${i}`))
  } catch (err) {
    return source
  }
}

/**
 * Inline viewer for a text file (code solutions, answer keys, mission logs).
 *
 * "Copy" and "Save" act on contents already in hand, so neither needs another
 * request — and neither mints a URL that could outlive the dialog.
 */
function CodeViewerDialog({ file, onClose }) {
  const classes = useStyles()
  const [copied, setCopied] = React.useState(false)

  React.useEffect(() => setCopied(false), [file])

  if (!file) {
    return null
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(file.contents)
      setCopied(true)
    } catch (err) {
      setCopied(false)
    }
  }

  const save = () => {
    const blob = new Blob([file.contents], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const subtitle = file.meta ?
    `Mission ${file.meta.missionNum}: ${file.meta.missionTitle} · Objective ${file.meta.objNum}` :
    ''

  const isPython = file.ext === 'py' || /\.py$/i.test(file.name || '')

  return (
    <Dialog open fullWidth maxWidth='md' onClose={onClose} aria-labelledby='viewer-title'>
      <DialogTitle id='viewer-title' disableTypography>
        <Typography variant='h6'>{file.name}</Typography>
        {subtitle &&
          <Typography variant='caption' color='textSecondary'>{subtitle}</Typography>}
      </DialogTitle>
      <DialogContent dividers>
        {file.loading ?
          <div className={classes.loading} role='status'>
            <CircularProgress size={28} color='secondary' />
            <Typography variant='body2' color='textSecondary'>{'Loading file…'}</Typography>
          </div> :
          <pre className={classes.code}>
            {isPython ? highlightPython(file.contents || '') : file.contents}
          </pre>}
      </DialogContent>
      <DialogActions>
        <Typography variant='caption' color='textSecondary' className={classes.hint}>
          {copied ? 'Copied to clipboard' : 'Reference solution — not visible to students'}
        </Typography>
        <Button onClick={copy} disabled={Boolean(file.loading)}>{'Copy'}</Button>
        <Button onClick={save} disabled={Boolean(file.loading)}>{'Save as file'}</Button>
        <Button onClick={onClose} variant='contained' color='primary'>{'Close'}</Button>
      </DialogActions>
    </Dialog>
  )
}

export { CodeViewerDialog }
